import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose } from 'react-apollo';
import { Link } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import TablePagination from '@material-ui/core/TablePagination';
import { FaEdit, FaTrash, FaPlus, FaArrowLeft } from 'react-icons/fa';

import Button from '../../../../components/CustomButtons/Button';
import { Dialog } from '../../../../components';
import {
  getMerchantUsersByAdmin,
  getRegionsByMerchantAdmin,
  getMerchantName,
} from './queries';
import {
  createMerchantUserByAdmin,
  updateMerchantUserByAdmin,
  removeMerchantUserByAdmin,
} from './mutations';
import styles from './styles';
import { colors } from '../../../../constants';

const ROLES = ['OWNER', 'MANAGER', 'ADVERTISER', 'DESIGNER', 'AGENT'];

const emptyForm = () => ({
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  telephone: '',
  mobile: '',
  password: '',
  confirmPassword: '',
  role: 'MANAGER',
  active: true,
  wallet: false,
  transfers: false,
  sendCoins: false,
  invite: true,
  storeIds: [],
  regionIds: [],
});

const CustomTableCell = withStyles(theme => ({
  head: {
    backgroundColor: colors.parimary,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

class MerchantUsers extends Component {
  state = {
    page: 0,
    limit: 15,
    openDialog: false,
    openConfirm: false,
    dialogTitle: '',
    editingUser: null,
    deleteUser: null,
    form: emptyForm(),
    messageOpen: false,
    messageTitle: '',
    messageContent: '',
  };

  getMerchantId = () => Number(this.props.match.params.merchantId);

  openCreate = () => {
    this.setState({
      openDialog: true,
      dialogTitle: 'Create User',
      editingUser: null,
      form: emptyForm(),
    });
  };

  openEdit = (user) => {
    const storeIds = (user.stores || []).map((s) => s.id);
    const regionIds = (user.regions || []).map((r) => r.id);
    this.setState({
      openDialog: true,
      dialogTitle: 'Edit User',
      editingUser: user,
      form: {
        username: user.username || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        telephone: user.telephone || '',
        mobile: user.mobile || '',
        password: '',
        confirmPassword: '',
        role: user.role || 'MANAGER',
        active: user.active !== false,
        wallet: !!(user.userView && user.userView.wallet),
        transfers: !!(user.userView && user.userView.transfers),
        sendCoins: !!(user.userView && user.userView.sendCoins),
        invite: false,
        storeIds,
        regionIds,
      },
    });
  };

  openDelete = (user) => {
    this.setState({ openConfirm: true, deleteUser: user });
  };

  handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    this.setState({
      form: {
        ...this.state.form,
        [name]: type === 'checkbox' ? checked : value,
      },
    });
  };

  handleRoleChange = (event) => {
    const role = event.target.value;
    const updates = { role };
    if (role === 'OWNER') {
      updates.wallet = true;
      updates.transfers = true;
      updates.sendCoins = true;
    }
    this.setState({ form: { ...this.state.form, ...updates } });
  };

  handleStoreToggle = (storeId, regionId) => {
    const { storeIds, regionIds } = this.state.form;
    const hasStore = storeIds.includes(storeId);
    const nextStoreIds = hasStore
      ? storeIds.filter((id) => id !== storeId)
      : [...storeIds, storeId];
    const nextRegionIds = hasStore
      ? regionIds.filter((id) => id !== regionId)
      : regionIds.includes(regionId) ? regionIds : [...regionIds, regionId];
    this.setState({ form: { ...this.state.form, storeIds: nextStoreIds, regionIds: nextRegionIds } });
  };

  showMessage = (title, content) => {
    this.setState({ messageOpen: true, messageTitle: title, messageContent: content });
  };

  onSaveUser = async () => {
    const { form, editingUser } = this.state;
    const {
      createMerchantUserByAdmin: createUser,
      updateMerchantUserByAdmin: updateUser,
      refetchUsers,
    } = this.props;

    if (!form.username || !form.firstName || !form.lastName || !form.email || !form.telephone || !form.role) {
      this.showMessage('Validation', 'Please complete required fields');
      return;
    }

    if (!editingUser) {
      if (!form.password || form.password !== form.confirmPassword) {
        this.showMessage('Validation', 'Passwords must match');
        return;
      }
    } else if (form.password && form.password !== form.confirmPassword) {
      this.showMessage('Validation', 'Passwords must match');
      return;
    }

    const input = {
      username: form.username,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      telephone: form.telephone,
      mobile: form.mobile || '',
      role: form.role,
      active: form.active,
      invite: form.invite,
      wallet: form.wallet,
      transfers: form.transfers,
      sendCoins: form.sendCoins,
      storeIds: form.storeIds,
      regionIds: form.regionIds,
    };

    if (!editingUser) {
      input.countryCode = 'US';
      input.languageCode = 'en';
      input.timezone = 'America/New_York';
      input.timeFormat = 'hh:mm A';
    }

    if (form.password) {
      input.password = form.password;
    }

    try {
      if (editingUser) {
        await updateUser({
          variables: { input: { ...input, id: editingUser.id } },
        });
      } else {
        await createUser({
          variables: { merchantId: this.getMerchantId(), input },
        });
      }
      await refetchUsers();
      this.setState({ openDialog: false, editingUser: null, form: emptyForm() });
      this.showMessage('Completed', editingUser ? 'User updated' : 'User created');
    } catch (error) {
      const message = error.graphQLErrors && error.graphQLErrors[0]
        ? error.graphQLErrors[0].message
        : 'Error saving user';
      this.showMessage('Error', message);
    }
  };

  onConfirmDelete = async () => {
    const { deleteUser } = this.state;
    const { removeMerchantUserByAdmin: removeUser, refetchUsers } = this.props;
    if (!deleteUser) return;
    try {
      await removeUser({ variables: { id: deleteUser.id } });
      await refetchUsers();
      this.setState({ openConfirm: false, deleteUser: null });
      this.showMessage('Completed', 'User deleted');
    } catch (error) {
      const message = error.graphQLErrors && error.graphQLErrors[0]
        ? error.graphQLErrors[0].message
        : 'Error deleting user';
      this.showMessage('Error', message);
    }
  };

  renderFormDialog = () => {
    const { classes, regions } = this.props;
    const { form, editingUser } = this.state;
    const isOwner = form.role === 'OWNER';

    return (
      <div>
        <div className={classes.formRow}>
          <TextField className={classes.formField} label="Username" name="username" value={form.username} onChange={this.handleFormChange} required />
          <FormControl className={classes.formField}>
            <InputLabel shrink>Role</InputLabel>
            <Select name="role" value={form.role} onChange={this.handleRoleChange}>
              {ROLES.map((role) => <MenuItem key={role} value={role}>{role}</MenuItem>)}
            </Select>
          </FormControl>
        </div>
        <div className={classes.formRow}>
          <TextField className={classes.formField} label="First Name" name="firstName" value={form.firstName} onChange={this.handleFormChange} required />
          <TextField className={classes.formField} label="Last Name" name="lastName" value={form.lastName} onChange={this.handleFormChange} required />
        </div>
        <TextField fullWidth label="Email" name="email" value={form.email} onChange={this.handleFormChange} margin="normal" required />
        <div className={classes.formRow}>
          <TextField className={classes.formField} label="Phone" name="telephone" value={form.telephone} onChange={this.handleFormChange} required />
          <TextField className={classes.formField} label="Mobile" name="mobile" value={form.mobile} onChange={this.handleFormChange} />
        </div>
        <div className={classes.formRow}>
          <TextField className={classes.formField} label={editingUser ? 'New Password' : 'Password'} name="password" type="password" value={form.password} onChange={this.handleFormChange} />
          <TextField className={classes.formField} label="Confirm Password" name="confirmPassword" type="password" value={form.confirmPassword} onChange={this.handleFormChange} />
        </div>
        {!isOwner && (
          <div className={classes.storesBox}>
            <Typography variant="subtitle2">Regions & Stores</Typography>
            {(regions || []).map((region) => (
              <div key={region.id}>
                <Typography variant="caption">{region.name}</Typography>
                {(region.stores || []).map((store) => (
                  <FormControlLabel
                    key={store.id}
                    control={
                      <Checkbox
                        checked={form.storeIds.includes(store.id)}
                        onChange={() => this.handleStoreToggle(store.id, region.id)}
                      />
                    }
                    label={store.name}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
        <FormControlLabel control={<Checkbox name="active" checked={form.active} onChange={this.handleFormChange} />} label="Active" />
        {!isOwner && (
          <>
            <FormControlLabel control={<Checkbox name="wallet" checked={form.wallet} onChange={this.handleFormChange} />} label="Wallet" />
            <FormControlLabel control={<Checkbox name="transfers" checked={form.transfers} onChange={this.handleFormChange} />} label="Transfers" />
            <FormControlLabel control={<Checkbox name="sendCoins" checked={form.sendCoins} onChange={this.handleFormChange} />} label="Send Coins" />
          </>
        )}
        <FormControlLabel control={<Checkbox name="invite" checked={form.invite} onChange={this.handleFormChange} />} label="Send invite email" />
      </div>
    );
  };

  render() {
    const { classes, users, merchant, loading } = this.props;
    const {
      page, limit, openDialog, dialogTitle, openConfirm, deleteUser,
      messageOpen, messageTitle, messageContent,
    } = this.state;
    const merchantId = this.getMerchantId();
    const merchantLabel = merchant
      ? (merchant.businessName || merchant.username)
      : `Merchant #${merchantId}`;
    const data = users || [];
    const pageData = data.slice(page * limit, page * limit + limit);

    if (loading && !users) return null;

    return (
      <div>
        <Dialog title={messageTitle} content={messageContent} open={messageOpen} onOk={() => this.setState({ messageOpen: false })} />
        <Dialog
          title={dialogTitle}
          open={openDialog}
          onOk={this.onSaveUser}
          onCancel={() => this.setState({ openDialog: false, editingUser: null })}
          listChildren={this.renderFormDialog()}
        />
        <Dialog
          title="Delete User"
          content={deleteUser ? `Delete ${deleteUser.username} (${deleteUser.role})?` : ''}
          open={openConfirm}
          onOk={this.onConfirmDelete}
          onCancel={() => this.setState({ openConfirm: false, deleteUser: null })}
        />
        <div className={classes.toolbar}>
          <div>
            <Link to={{ pathname: `/detailMerchant/${merchantId}` }}>
              <Button className={classes.button}>
                <Typography component="p" className={classes.text}>Back</Typography>
                <FaArrowLeft color="#ffffff" size={14} />
              </Button>
            </Link>
            <Typography component="h4" style={{ marginTop: 8 }}>
              Users - {merchantLabel}
            </Typography>
          </div>
          <Button className={classes.button} onClick={this.openCreate}>
            <Typography component="p" className={classes.text}>Create User</Typography>
            <FaPlus color="#ffffff" size={14} />
          </Button>
        </div>
        <Paper className={classes.root}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <CustomTableCell>Username</CustomTableCell>
                <CustomTableCell>Name</CustomTableCell>
                <CustomTableCell>Email</CustomTableCell>
                <CustomTableCell>Role</CustomTableCell>
                <CustomTableCell>Active</CustomTableCell>
                <CustomTableCell align="center">Actions</CustomTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.map((user) => (
                <TableRow className={classes.row} key={user.id}>
                  <CustomTableCell component="th" scope="row">{user.username}</CustomTableCell>
                  <CustomTableCell>{`${user.firstName || ''} ${user.lastName || ''}`.trim()}</CustomTableCell>
                  <CustomTableCell>{user.email}</CustomTableCell>
                  <CustomTableCell>{user.role}</CustomTableCell>
                  <CustomTableCell>{user.active === false ? 'No' : 'Yes'}</CustomTableCell>
                  <CustomTableCell align="center">
                    <Button className={classes.button} onClick={() => this.openEdit(user)}>
                      <FaEdit color="#ffffff" size={14} />
                    </Button>
                    <Button className={classes.button} onClick={() => this.openDelete(user)}>
                      <FaTrash color="#ffffff" size={14} />
                    </Button>
                  </CustomTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[15, 25, 50]}
            component="div"
            count={data.length}
            rowsPerPage={limit}
            page={page}
            onChangePage={(e, p) => this.setState({ page: p })}
            onChangeRowsPerPage={(e) => this.setState({ limit: e.target.value, page: 0 })}
          />
        </Paper>
      </div>
    );
  }
}

MerchantUsers.propTypes = {
  classes: PropTypes.object.isRequired,
  users: PropTypes.array,
  regions: PropTypes.array,
  merchant: PropTypes.object,
  loading: PropTypes.bool,
  createMerchantUserByAdmin: PropTypes.func.isRequired,
  updateMerchantUserByAdmin: PropTypes.func.isRequired,
  removeMerchantUserByAdmin: PropTypes.func.isRequired,
  refetchUsers: PropTypes.func,
};

const withUsers = graphql(getMerchantUsersByAdmin, {
  options: (props) => ({
    variables: { merchantId: Number(props.match.params.merchantId) },
    fetchPolicy: 'network-only',
  }),
  props: ({ data }) => ({
    loading: data.loading,
    users: data.getMerchantUsersByAdmin,
    refetchUsers: data.refetch,
  }),
});

const withRegions = graphql(getRegionsByMerchantAdmin, {
  options: (props) => ({
    variables: { merchantId: Number(props.match.params.merchantId) },
    fetchPolicy: 'cache-first',
  }),
  props: ({ data }) => ({
    regions: data.getRegionsByMerchantAdmin,
  }),
});

const withMerchant = graphql(getMerchantName, {
  options: (props) => ({
    variables: { id: Number(props.match.params.merchantId) },
  }),
  props: ({ data }) => ({
    merchant: data.getUserById,
  }),
});

const withCreate = graphql(createMerchantUserByAdmin, { name: 'createMerchantUserByAdmin' });
const withUpdate = graphql(updateMerchantUserByAdmin, { name: 'updateMerchantUserByAdmin' });
const withRemove = graphql(removeMerchantUserByAdmin, { name: 'removeMerchantUserByAdmin' });

export default withStyles(styles)(compose(
  withUsers,
  withRegions,
  withMerchant,
  withCreate,
  withUpdate,
  withRemove,
)(MerchantUsers));
